import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListContentResult, type OpenAbTestResult } from '@whinerp/client';
import { formatCount, formatDateTime, formatPercent } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * A/B test on a campaign (F-066): the variants while it is a draft, and the
 * results once it is sending - rate, lift against the original, and how sure
 * the leader is. A winner is never shown before the minimum sample; the reason
 * is shown instead.
 */

type Test = NonNullable<OpenAbTestResult['test']>;
type VariantDraft = { label: string; subject: string; documentId: string };
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';
const LABELS = ['A', 'B', 'C', 'D'];

export function AbTestPanel({ campaignId, campaignStatus, documentId }: { campaignId: string; campaignStatus: string; documentId: string | null }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-066');
  const opened = useQuery<OpenAbTestResult, ApiError>({
    queryKey: ['ab-test', campaignId],
    queryFn: () => api.openAbTest({ path: { campaignId }, body: {} }),
    enabled: enabled.enabled,
  });
  const emails = useQuery<ListContentResult, ApiError>({
    queryKey: ['content', 'email'],
    queryFn: () => api.listContent({ query: { kind: 'email' } }),
    enabled: enabled.enabled,
  });

  const [setting, setSetting] = useState(false);
  const [kind, setKind] = useState<'subject' | 'content'>('subject');
  const [metric, setMetric] = useState<'opened' | 'clicked'>('opened');
  const [testPercent, setTestPercent] = useState('20');
  const [minimumSample, setMinimumSample] = useState('100');
  const [windowHours, setWindowHours] = useState('4');
  const [variants, setVariants] = useState<VariantDraft[]>([
    { label: 'A', subject: '', documentId: documentId ?? '' },
    { label: 'B', subject: '', documentId: '' },
  ]);

  const test = opened.data?.test ?? null;
  useEffect(() => {
    if (!test) return;
    setKind(test.kind);
    setMetric(test.metric);
    setTestPercent(String(test.testPercent));
    setMinimumSample(String(test.minimumSample));
    setWindowHours(String(test.windowHours));
    setVariants(test.variants.map((variant) => ({ label: variant.label, subject: variant.subject ?? '', documentId: variant.documentId ?? '' })));
  }, [test]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ab-test', campaignId] });
    await queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
  };
  const save = useMutation({
    mutationFn: () =>
      api.saveAbTest({
        path: { campaignId },
        body: {
          kind,
          metric,
          testPercent: Number.parseInt(testPercent, 10),
          minimumSample: Number.parseInt(minimumSample, 10),
          windowHours: Number.parseInt(windowHours, 10),
          variants: variants.map((variant) => ({
            label: variant.label,
            subject: kind === 'subject' ? variant.subject : null,
            documentId: kind === 'content' ? variant.documentId || null : null,
          })),
        },
      }),
    onSuccess: async () => {
      setSetting(false);
      await refresh();
    },
  });
  const remove = useMutation({ mutationFn: () => api.removeAbTest({ path: { campaignId }, body: {} }), onSuccess: refresh });
  const decide = useMutation({ mutationFn: () => api.decideAbTest({ path: { campaignId }, body: {} }), onSuccess: refresh });

  if (!enabled.enabled) return null;
  if (opened.isPending) return <Skeleton className="h-block-sm w-full" />;
  const refusal = opened.error ?? save.error ?? remove.error ?? decide.error;
  const editable = campaignStatus === 'draft' && (!test || test.status === 'draft');
  const pct = (value: number) => formatPercent(value, format, { decimals: 1 });

  return (
    <Panel data-region="ab-test">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('ab.title')}</SectionLabel>
          {test ? <span data-ab-status className="text-meta text-ink-meta">{t(`ab.status.${test.status}`)}</span> : null}
          {editable && !setting ? (
            <Button intent="secondary" size="small" data-ab-setup className="ms-auto" onClick={() => setSetting(true)}>
              {test ? t('ab.change') : t('ab.setUp')}
            </Button>
          ) : null}
        </div>
        {refusal ? <p data-ab-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}

        {!test && !setting ? <p className="text-meta text-ink-meta">{t('ab.none')}</p> : null}

        {setting ? (
          <div data-ab-form className="flex flex-col gap-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('ab.kind')}
                <select data-ab-kind className={INPUT} value={kind} onChange={(e) => setKind(e.target.value as 'subject' | 'content')}>
                  <option value="subject">{t('ab.kind.subject')}</option>
                  <option value="content">{t('ab.kind.content')}</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('ab.metric')}
                <select data-ab-metric className={INPUT} value={metric} onChange={(e) => setMetric(e.target.value as 'opened' | 'clicked')}>
                  <option value="opened">{t('ab.metric.opened')}</option>
                  <option value="clicked">{t('ab.metric.clicked')}</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('ab.testPercent')}
                <input data-ab-percent inputMode="numeric" className={INPUT} value={testPercent} onChange={(e) => setTestPercent(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('ab.minimumSample')}
                <input data-ab-sample inputMode="numeric" className={INPUT} value={minimumSample} onChange={(e) => setMinimumSample(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('ab.windowHours')}
                <input data-ab-window inputMode="numeric" className={INPUT} value={windowHours} onChange={(e) => setWindowHours(e.target.value)} />
              </label>
            </div>
            <ul className="flex flex-col gap-2">
              {variants.map((variant, index) => (
                <li key={variant.label} data-ab-variant={variant.label} className="flex flex-wrap items-center gap-2">
                  <span className="w-8 text-dense text-ink">{variant.label}</span>
                  {kind === 'subject' ? (
                    <input
                      data-ab-variant-subject
                      aria-label={t('ab.subjectFor', { values: { label: variant.label } })}
                      className={`${INPUT} min-w-0 flex-1`}
                      value={variant.subject}
                      onChange={(e) => setVariants((all) => all.map((v, i) => (i === index ? { ...v, subject: e.target.value } : v)))}
                    />
                  ) : (
                    <select
                      data-ab-variant-document
                      aria-label={t('ab.emailFor', { values: { label: variant.label } })}
                      className={`${INPUT} min-w-0 flex-1`}
                      value={variant.documentId}
                      onChange={(e) => setVariants((all) => all.map((v, i) => (i === index ? { ...v, documentId: e.target.value } : v)))}
                    >
                      <option value="">{t('ab.chooseEmail')}</option>
                      {(emails.data?.documents ?? []).map((doc) => (
                        <option key={doc.documentId} value={doc.documentId}>{doc.name}</option>
                      ))}
                    </select>
                  )}
                  {index >= 2 ? (
                    <Button intent="ghost" size="small" onClick={() => setVariants((all) => all.filter((_, i) => i !== index))}>
                      {t('ab.removeVariant')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              {variants.length < 4 ? (
                <Button
                  intent="ghost"
                  size="small"
                  data-ab-add-variant
                  onClick={() => setVariants((all) => [...all, { label: LABELS[all.length] ?? String(all.length + 1), subject: '', documentId: '' }])}
                >
                  {t('ab.addVariant')}
                </Button>
              ) : null}
              <Button intent="primary" size="small" data-ab-save disabled={save.isPending} onClick={() => save.mutate()}>
                {t('ab.save')}
              </Button>
              <Button intent="ghost" size="small" onClick={() => setSetting(false)}>
                {t('ab.cancel')}
              </Button>
              {test ? (
                <Button intent="ghost" size="small" data-ab-remove disabled={remove.isPending} onClick={() => remove.mutate()}>
                  {t('ab.remove')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {test && !setting ? <AbResults test={test} pct={pct} /> : null}

        {test && test.status === 'testing' ? (
          <div className="flex flex-wrap items-center gap-2">
            {test.decidesAt ? (
              <span className="text-meta text-ink-meta">
                {t('ab.decidesAt', { values: { at: formatDateTime(test.decidesAt, format), held: formatCount(test.heldForWinner, format) } })}
              </span>
            ) : null}
            <Button intent="secondary" size="small" data-ab-decide disabled={decide.isPending} onClick={() => decide.mutate()}>
              {t('ab.decide')}
            </Button>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

function AbResults({ test, pct }: { test: Test; pct: (value: number) => string }): JSX.Element {
  const t = useT();
  const format = useFormat();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-meta text-ink-meta">
        {t('ab.summary', {
          values: {
            percent: String(test.testPercent),
            metric: t(`ab.metric.${test.metric}`),
            minimum: formatCount(test.minimumSample, format),
          },
        })}
      </p>
      <div data-scroll-container className="overflow-x-auto">
      <table data-ab-results className="w-full text-dense">
        <thead>
          <tr className="text-start text-meta text-ink-meta">
            <th className="py-1 text-start font-normal">{t('ab.variant')}</th>
            <th className="py-1 text-end font-normal">{t('ab.sent')}</th>
            <th className="py-1 text-end font-normal">{t(`ab.metric.${test.metric}`)}</th>
            <th className="py-1 text-end font-normal">{t('ab.rate')}</th>
            <th className="py-1 text-end font-normal">{t('ab.lift')}</th>
          </tr>
        </thead>
        <tbody>
          {test.variants.map((variant) => (
            <tr key={variant.variantId} data-ab-result={variant.label} className="border-t border-t-rule">
              <td className="py-1 text-ink">
                {variant.label} · {variant.subject ?? variant.documentName ?? ''}
                {test.winnerVariantId === variant.variantId ? (
                  <span data-ab-winner className="ms-2 rounded-control bg-success-tint px-1 text-meta text-success-text">
                    {test.status === 'decided' ? t('ab.winner') : t('ab.sentToRest')}
                  </span>
                ) : null}
              </td>
              <td className="py-1 text-end text-ink">{formatCount(variant.sent, format)}</td>
              <td className="py-1 text-end text-ink">{formatCount(variant.converted, format)}</td>
              <td className="py-1 text-end text-ink">{pct(variant.rate)}</td>
              <td className="py-1 text-end text-ink">{variant.lift === null ? '—' : pct(variant.lift)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {test.confidence !== null ? (
        <div data-ab-confidence className="flex items-center gap-2">
          <span className="text-meta text-ink-meta">{t('ab.confidence')}</span>
          <span className="h-2 flex-1 rounded-control bg-canvas">
            <span
              className={test.confidence >= 0.95 ? 'block h-2 rounded-control bg-success' : 'block h-2 rounded-control bg-warning'}
              style={{ width: `${String(Math.round(test.confidence * 100))}%` }}
            />
          </span>
          <span className="text-dense text-ink">{pct(test.confidence)}</span>
        </div>
      ) : null}
      {test.reason ? <p data-ab-reason className="text-dense text-ink">{test.reason}</p> : null}
    </div>
  );
}
